import { useTranslation } from 'next-i18next';
import { useContext, useEffect, useState, VFC } from 'react';
import ReactModal from 'react-modal';

import { connectToMetamask } from '@/apis/blockchain/blockchainCommon';
import CustomButton from '@/components/CustomButton';
import DefaultSpinner from '@/components/DefaultSpinner';
import { Web3Context } from '@/contexts/Web3Provider/Web3Provider';
import useAccessToken from '@/hooks/useAccessToken';
import useCurrentUser from '@/hooks/useCurrentUser';
import TimesIcon from '@/icons/times.svg';
import { captureException } from '@/utils/Sentry';

import styles from './AccessTokenRequired.module.scss';

const SignatureRequestModal: VFC = () => {
  const { t } = useTranslation();
  const [isImmediateConnectRequestDone, setIsImmediateConnectRequestDone] =
    useState(false);
  const [isImmediateSignRequestDone, setIsImmediateSignRequestDone] =
    useState(false);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [isMetamaskOpen, setIsMetamaskOpen] = useState(false);
  const [showModal, setShowModal] = useState(true);

  const {
    currentAccountAddress,
    web3: { metamaskInstalled, metamaskProvider },
  } = useContext(Web3Context);
  const { user } = useCurrentUser();
  const { accessToken, requestSignature } = useAccessToken();

  const requestToConnectMetamask = async () => {
    if (isMetamaskOpen) return;

    setIsMetamaskOpen(true);

    try {
      await connectToMetamask(metamaskProvider);
      window.location.reload();
    } catch (err) {
      captureException(err);
    } finally {
      setIsMetamaskOpen(false);
    }
  };

  const openSignaturePrompt = async () => {
    if (isPromptOpen) return;

    setIsPromptOpen(true);

    try {
      await requestSignature();
    } catch (err) {
      captureException(err);
    } finally {
      setIsPromptOpen(false);
    }
  };

  const triggerImmediateRequest = () => {
    if (!isImmediateConnectRequestDone && !currentAccountAddress) {
      // request to connect metamask immediately
      setIsImmediateConnectRequestDone(true);
      requestToConnectMetamask();
      return;
    }

    if (!isImmediateSignRequestDone && !accessToken) {
      // `user` is necessary for singing
      if (!user) return;

      // open signature prompt immediately
      setIsImmediateSignRequestDone(true);
      openSignaturePrompt();
    }
  };

  useEffect(triggerImmediateRequest, [triggerImmediateRequest]);

  return (
    <ReactModal
      isOpen={showModal}
      shouldCloseOnEsc={false}
      shouldCloseOnOverlayClick={false}
      className={styles.authenticationModal}
    >
      <div className={styles.authenticationScreen}>
        <div className={styles.closeIcon}>
          <TimesIcon
            height={24}
            width={24}
            onClick={() => setShowModal(false)}
          />
        </div>
        <h1>{t('accessTokenRequired.authenticationScreen.heading')}</h1>
        <p>{t('accessTokenRequired.authenticationScreen.message')}</p>
        <p>
          <a
            className={styles.annotationLink}
            href="https://tasteful-quit-93d.notion.site/About-Signature-9e44e444e4c346aab869cdbb2bdd4690"
            target="_blank"
            rel="noreferrer"
          >
            {t('accessTokenRequired.authenticationScreen.annotation')}
          </a>
        </p>
        {!metamaskInstalled ? (
          <CustomButton>
            <a
              href={process.env.NEXT_PUBLIC_METAMASK_SITE}
              target="_blank"
              rel="noreferrer"
              className={styles.customButtonLink}
            >
              {t('accessTokenRequired.authenticationScreen.installMetaMask')}
            </a>
          </CustomButton>
        ) : !currentAccountAddress ? (
          <CustomButton
            onClick={requestToConnectMetamask}
            loading={isMetamaskOpen}
          >
            {t('accessTokenRequired.authenticationScreen.connectMetaMask')}
          </CustomButton>
        ) : !user ? (
          // wait for loading current user because it's necessary for signing
          <DefaultSpinner />
        ) : (
          <CustomButton onClick={openSignaturePrompt} loading={isPromptOpen}>
            {t('accessTokenRequired.authenticationScreen.signWithMetaMask')}
          </CustomButton>
        )}

        {isMetamaskOpen && (
          <p>
            {t('accessTokenRequired.authenticationScreen.seeMetaMaskPrompt')}
          </p>
        )}
        {isPromptOpen && (
          <p>
            {t(
              'accessTokenRequired.authenticationScreen.seeMetaMaskSignaturePrompt'
            )}
          </p>
        )}
      </div>
    </ReactModal>
  );
};

export default SignatureRequestModal;
